import { usePermissionStore } from '@Models/usePermissionModel';
import { useReportTabsStore } from '@Models/useReportTabsModel';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { Button, Flex, useColorMode, useColorStyle } from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingBtn from '@Components/Common/LoadingBtn';
import { PermissionNameEnum } from '@Types/permissions';
import ViewPDFSampleButton from '../../ViewPDFSampleButton';
import { useOneTimeReportStore } from '../store';
import { DataPeriodState } from '../ReportContent/ReportDataPeriod/datePeriod/type';
import { IntervalType } from '@Utils/enums';
import { dayjs } from '@Utils/time';
import { FormatContent, ReportContentItemsKey } from '@Types/report';
import { ReportApi } from '@Apis';
import i18next from 'i18next';
import { reportPendo } from '@Utils/setupPendo';
import { globalStoreGetter } from '@Utils/globalStoreGetter';

export const Footer = ({ children }: { children: React.ReactNode }) => {
  const { siderWidth } = useSiderWidthStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Flex
      position="fixed"
      pl="5x"
      bottom={0}
      left={siderWidth}
      right={0}
      background={colorStyle.background.secondary}
      height={64}
      align="center"
      justify="flex-start"
      gap="8px"
    >
      {children}
    </Flex>
  );
};

export const Reset = ({ onReset }: { onReset: () => void }) => {
  const { t } = useTranslation();
  const reset = useOneTimeReportStore((store) => store.reset);

  return (
    <Button
      onClick={() => {
        reset();
        onReset();
      }}
    >
      {t('reportCommon.buttons.reset')}
    </Button>
  );
};

// locating to error position
const scrollToError = (elementId: string) => {
  const errorBoundingClient =
    document.getElementById(elementId)?.getBoundingClientRect().top || 0;
  const headerBoundingClient =
    document.getElementById('generalSettingsTitle')?.getBoundingClientRect()
      .top || 0;
  (document.querySelector('.app-body') as Element).scrollTop =
    errorBoundingClient - headerBoundingClient - window.innerHeight / 2;
};

function getPeriodWithOffset(period: DataPeriodState) {
  if (period.dataPeriod !== 'custom') return period;
  let lte: string;

  // dayjs.tz("2013-11-18T12:34:56", "+10:00") // '2013-11-18T12:34:56+10:00'
  if (period.timeRange.interval === IntervalType.Day) {
    lte = dayjs
      .tz(period.timeRange.lte, period.timezoneOffset)
      .add(1, 'day')
      .add(-1, 'millisecond')
      .toISOString();
  } else if (period.timeRange.interval === IntervalType.Month) {
    lte = dayjs
      .tz(period.timeRange.lte, period.timezoneOffset)
      .add(1, 'month')
      .add(-1, 'millisecond')
      .toISOString();
  } else {
    lte = dayjs
      .tz(period.timeRange.lte, period.timezoneOffset)
      .add(-1, 'millisecond')
      .toISOString();
  }

  return {
    ...period,
    timeRange: {
      interval: period.timeRange.interval,
      gte: dayjs.tz(period.timeRange.gte, period.timezoneOffset).toISOString(),
      lte,
    },
  };
}

export const Submit = ({
  formatContent,
  setFormatContent,
  resetReportData,
}: {
  formatContent: FormatContent;
  setFormatContent: (v: FormatContent) => void;
  resetReportData: () => void;
}) => {
  const { t } = useTranslation();
  const { widthEditablePermission } = usePermissionStore();
  const enable = widthEditablePermission(PermissionNameEnum.report);

  const { setTabStatus } = useReportTabsStore();

  const getGeneralSetting = useOneTimeReportStore(
    (store) => store.getGeneralSetting
  );

  const getReportContent = useOneTimeReportStore(
    (store) => store.getReportContent
  );

  const getReportNotification = useOneTimeReportStore(
    (store) => store.getReportNotification
  );

  const getReportFormat = useOneTimeReportStore(
    (store) => store.getReportFormat
  );

  const getError = useOneTimeReportStore((store) => store.getError);

  const submitData = async function () {
    const {
      name: reportName,
      desc: description,
      services: checkedServices,
      orgId,
    } = getGeneralSetting();

    const { template, period, sections } = getReportContent();

    const { subject, message, recipients } = getReportNotification();

    const { format } = getReportFormat();

    const { recipientError } = getError();
    //validate
    if (reportName.trim() === '') {
      scrollToError('reportName');
      return;
    }
    if (formatContent.fileInfo?.fileError) {
      scrollToError('uploadLogoButton');
      return;
    }
    if (recipientError) {
      scrollToError('recipient-select');
      return;
    }
    if (
      formatContent.isCompanyLogoDisplay &&
      !formatContent.fileInfo.fileName
    ) {
      setFormatContent({
        ...formatContent,
        fileInfo: {
          ...formatContent.fileInfo,
          fileError: 'required',
        },
      });
      scrollToError('uploadLogoButton');
      return;
    }
    //start submit
    const {
      coverDescription,
      companyName,
      isCompanyLogoDisplay,
      fileInfo,
      isTrendLogoDisplay,
      isDefaultSettingSave,
    } = formatContent;

    let storageId = fileInfo?.storageId;
    if (!storageId && fileInfo.fileData) {
      const saveLogoRes = await ReportApi.saveLogo(fileInfo.fileData);
      if (saveLogoRes.data?.storageId) {
        storageId = saveLogoRes.data?.storageId;
      } else {
        window.showNotification({
          titleMsg: t('reportCommon.alert.createReport.fail'),
          bodyMsg: t('reportCommon.alert.createReport.failDesc'),
          severity: 'error',
        });
        return;
      }
    }

    const formatSetting = {
      companyName,
      companyLogo: {
        displayLogo: isCompanyLogoDisplay,
        fileName: fileInfo?.fileName,
        fileType: fileInfo?.fileType,
        fileSize: fileInfo?.fileSize,
        storageId,
      },
      displayTrendLogo: isTrendLogoDisplay,
    };
    if (isDefaultSettingSave) {
      await ReportApi.saveDefaultSetting(formatSetting);
    }
    const params = {
      type: 'oneTime',
      name: reportName.trim(),
      description,
      templateName: template,
      servicesTypes: checkedServices,
      ...getPeriodWithOffset(period),
      content: (Object.keys(sections) as ReportContentItemsKey[]).filter(
        (key) => {
          return sections[key];
        }
      ),
      format,
      formatContent: {
        coverDescription,
        ...formatSetting,
      },
      //orgId,
      emailDeliveryInfo: {
        recipients,
        subject,
        message,
      },
      language: i18next.language,
      companyGuid: orgId,
    };
    try {
      const createReportRes = await ReportApi.createReport(params);
      if (createReportRes.data?.id) {
        setTabStatus({
          tabIndex: 0,
          submitSuccess: true,
        });
        resetReportData();
      }
    } catch (error: any) {
      if (
        error.data?.error?.details?.[0].code === 'REPORT;ONETIME_OUT_OF_LIMIT'
      ) {
        const limitation = error.data?.error?.details?.[0]?.limit || 20;
        window.showNotification({
          titleMsg: t('reportCommon.alert.createOneTime.fail'),
          bodyMsg: t('errors.REPORT;ONETIME_OUT_OF_LIMIT', {
            orgName: globalStoreGetter.getOrgsMap().get(orgId),
            limitation,
          }),
          severity: 'error',
        });
      } else if (error && error.status === 429) {
        window.showNotification({
          titleMsg: t('reportCommon.alert.createOneTime.fail'),
          bodyMsg: t('reportCommon.alert.createOneTime.failDesc'),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('reportCommon.alert.createReport.fail'),
          bodyMsg: t('reportCommon.alert.createReport.failDesc'),
          severity: 'error',
        });
      }
    } finally {
      reportPendo('Report.OneTime.Generated.Click', {
        sections: JSON.stringify(params.content),
        ...period,
      });
    }
  };

  return (
    <LoadingBtn disabled={!enable} variant="primary" onClick={submitData}>
      {(loading) =>
        loading
          ? t('reportCommon.buttons.creating')
          : t('reportCommon.buttons.create')
      }
    </LoadingBtn>
  );
};

export const PDFSample = () => {
  const template = useOneTimeReportStore(
    (store) => store.reportContent.template
  );
  const format = useOneTimeReportStore((store) => store.reportFormat.format);
  return (
    <>
      <ViewPDFSampleButton templateId={template} format={format} />
    </>
  );
};
